import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Download, FileText, ShieldCheck, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api, fmtPrice } from "@/lib/api";
import { usePageTitle, FAQS } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";
import { PackMockup, MiniDoc } from "@/components/site/PackMockup";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { NotifyModal } from "@/components/site/NotifyModal";

function ComingSoonPage({ product }) {
  usePageTitle(`${product.short_name} — Coming Soon | Sitebound`, product.tagline);
  return (
    <div data-testid="product-coming-soon" className="blueprint-grid relative overflow-hidden">
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-start justify-center px-4 py-24 sm:px-6 lg:px-8">
        <div className="mono-label">In production</div>
        <h1 className="font-display mt-4 text-5xl font-extrabold uppercase leading-[0.9] tracking-tight text-slate-100 sm:text-7xl">
          {product.short_name}
        </h1>
        <div className="font-display text-outline mt-2 text-5xl font-extrabold uppercase sm:text-7xl">
          Coming soon
        </div>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">{product.tagline}</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <NotifyModal
            slug={product.slug}
            productName={product.short_name}
            trigger={
              <button
                data-testid="product-notify-button"
                className="bg-[#FF5500] px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
              >
                Notify me at launch
              </button>
            }
          />
          <Link
            data-testid="product-back-to-shop"
            to="/shop"
            className="border border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-slate-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
          >
            Back to the range
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!product || product.status !== "live") return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.tagline,
      brand: { "@type": "Brand", name: "Sitebound" },
      offers: {
        "@type": "Offer",
        price: product.price,
        priceCurrency: "AUD",
        availability: "https://schema.org/InStock",
      },
    });
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, [product]);

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="mx-auto max-w-7xl px-4 py-32 text-center">
        <h1 className="font-display text-4xl font-bold uppercase text-slate-100">Pack not found</h1>
        <Link to="/shop" className="mt-6 inline-block text-sm uppercase tracking-widest text-[#FF5500]">
          Back to the range
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div data-testid="product-loading" className="mx-auto max-w-7xl px-4 py-32 text-sm uppercase tracking-widest text-slate-500">
        Loading pack…
      </div>
    );
  }

  if (product.status !== "live") return <ComingSoonPage product={product} />;

  return <LiveProduct product={product} />;
}

function StickyBuyBar({ product, anchorRef }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only once the main buy button has scrolled fully above the viewport.
    // A scroll listener (not IntersectionObserver) so fast flicks and jump-scrolls
    // that skip the CTA region entirely are still handled.
    const onScroll = () => {
      const el = anchorRef.current;
      if (!el) return;
      setVisible(el.getBoundingClientRect().bottom < 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [anchorRef]);

  return (
    <div
      data-testid="sticky-buy-bar"
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0A0B0D]/95 backdrop-blur-md transition-transform duration-300 md:hidden ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div
        className="flex items-center justify-between gap-4 px-4 pt-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div>
          <div className="font-mono-ind text-[9px] uppercase tracking-[0.2em] text-slate-500">
            {product.short_name}
          </div>
          <div className="font-display text-xl font-extrabold text-[#FF5500]">
            {fmtPrice(product.price)}{" "}
            <span className="text-xs font-semibold text-slate-500 line-through">
              {fmtPrice(product.compare_at)}
            </span>
          </div>
        </div>
        <CheckoutDialog
          product={product}
          trigger={
            <button
              data-testid="sticky-buy-button"
              tabIndex={visible ? 0 : -1}
              className="shrink-0 bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
            >
              Buy now
            </button>
          }
        />
      </div>
    </div>
  );
}

function LiveProduct({ product }) {
  usePageTitle(`${product.name} — $49 AUD | Sitebound`, product.tagline);
  const buyCtaRef = useRef(null);

  return (
    <div data-testid="product-page" className="pb-20 md:pb-0">
      <StickyBuyBar product={product} anchorRef={buyCtaRef} />
      <section className="relative overflow-hidden">
        <div className="spotlight absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
          <div className="font-mono-ind text-[10px] uppercase tracking-[0.25em] text-slate-500">
            Store <span className="text-[#FF5500]">/</span> {product.category}{" "}
            <span className="text-[#FF5500]">/</span> {product.short_name}
          </div>

          <div className="mt-10 grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
            <Reveal>
              <PackMockup testId="product-mockup" />
              <div className="mt-10 grid grid-cols-3 gap-3">
                <MiniDoc label="Resume" />
                <MiniDoc label="Guide" lines={5} />
                <MiniDoc label="Checklist" lines={3} />
              </div>
            </Reveal>

            <div>
              <Reveal>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-2 border border-[#FF5500]/40 bg-[#FF5500]/10 px-2.5 py-1">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#FF5500]" />
                    <span className="font-mono-ind text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF5500]">
                      Available now
                    </span>
                  </span>
                  <span className="font-mono-ind text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Digital download
                  </span>
                </div>
                <h1 className="font-display mt-5 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl">
                  {product.name}
                </h1>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
                  {product.description}
                </p>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="mt-8 flex items-end gap-4">
                  <span data-testid="product-price" className="font-display text-5xl font-extrabold text-[#FF5500]">
                    {fmtPrice(product.price)}
                  </span>
                  <span className="pb-1.5 text-lg text-slate-500 line-through">
                    {fmtPrice(product.compare_at)}
                  </span>
                </div>
                <div className="mt-6 space-y-2.5">
                  {[
                    { icon: Zap, text: "Instant access to your Sitebound Job Seeker Hub" },
                    { icon: FileText, text: `${product.stats?.pdfs ?? ""} guides to read online + ${(product.stats?.docx ?? 0) + (product.stats?.xlsx ?? 0)} editable templates to download` },
                    { icon: Download, text: "Full ZIP backup download also included" },
                    { icon: ShieldCheck, text: "Secure checkout · Apple Pay & Google Pay" },
                  ].map(({ icon: Icon, text }) => (
                    <p key={text} className="flex items-center gap-3 text-sm text-slate-300">
                      <Icon className="h-4 w-4 shrink-0 text-[#FF5500]" /> {text}
                    </p>
                  ))}
                </div>
                <div className="mt-8" ref={buyCtaRef}>
                  <CheckoutDialog
                    product={product}
                    trigger={
                      <button
                        data-testid="product-buy-button"
                        className="w-full bg-[#FF5500] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800] sm:w-auto"
                      >
                        Buy now — {fmtPrice(product.price)}
                      </button>
                    }
                  />
                  <p className="mt-3 text-xs text-slate-500">
                    Guest checkout — no account required.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="border-t border-white/10 bg-[#0D0F12] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mono-label">What's inside</div>
            <h2 className="font-display mt-4 text-4xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-5xl">
              {product.stats?.sections ?? product.components.length} sections. {product.stats?.files ?? ""} resources. One hub.
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">
              Every guide is readable online in your Job Seeker Hub on any device. Templates and
              trackers download as editable DOCX and XLSX files — and the complete ZIP backup is
              always available from your account.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 w-full" data-testid="components-accordion">
              {product.components.map((c, i) => (
                <AccordionItem key={c.name} value={`c-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`component-trigger-${i}`}
                    className="text-left hover:no-underline"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono-ind text-xs text-[#FF5500]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm font-semibold uppercase tracking-wide text-slate-200">
                        {c.name}
                      </span>
                      <span className="font-mono-ind hidden text-[10px] uppercase tracking-widest text-slate-500 sm:inline">
                        {c.format}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-9 text-sm leading-relaxed text-slate-400">
                    {c.blurb}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="mono-label">Who it's for</div>
            <h2 className="font-display mt-4 text-4xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-5xl">
              Built for your first start
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-4">
              {[
                "School leavers chasing their first construction or trade job",
                "Career changers moving into construction, civil or mining",
                "Workers targeting FIFO roles with no site experience",
                "Anyone sick of applications going nowhere",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-slate-300">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-[#FF5500]" /> {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 bg-[#0D0F12] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mono-label">Before you buy</div>
            <h2 className="font-display mt-4 text-4xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-5xl">
              Common questions
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 w-full">
              {FAQS.slice(0, 7).map((f, i) => (
                <AccordionItem key={i} value={`pf-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`product-faq-trigger-${i}`}
                    className="text-left text-sm font-semibold uppercase tracking-wide text-slate-200 hover:text-[#FF5500] hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
